/**
 * The guest's prize archive — every prize they've won, and whether it has
 * been collected yet.
 *
 * WHY THIS EXISTS (2026-10-08). A prize win used to live in exactly one place
 * the guest could see: the win modal in Jukebox4. Dismiss it and the QR was
 * gone for good. The win itself is recorded on the VENUE's request feed
 * (users/{venueUid}/djchats/{djchatId}), which the guest has no screen for,
 * and staff only wrote a copy to the guest AFTER redemption — so an
 * outstanding prize had no record on the guest's side at all.
 *
 * So a won prize is now saved to the guest's own profile, the moment it is
 * secured, holding everything needed to collect it later:
 *
 *   users/{uid}/prizes/{djchatId}
 *     status: 'outstanding' | 'redeemed'
 *     qr:     the exact QR payload staff scan
 *     code:   the same thing as typeable text
 *     ...prize, venue, track, when won / collected, by whom
 *
 * The doc id is the winning request's id, so saving twice is harmless and a
 * prize can never appear twice. Staff redemption (reports-admin
 * prizeRedemptionService) flips the SAME doc to 'redeemed' inside its
 * transaction.
 *
 * ONE SOURCE for the QR payload and the code: the win modal and the archive
 * both build them here, so the thing a guest is shown and the thing staff can
 * read can never drift apart again — which is exactly how the displayed code
 * ended up unparseable by the staff scanner.
 */
import {
  doc, setDoc, collection, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase-config.js';

export const PRIZE_QR_TYP = 'muuzbox-prize';

/** The QR staff scan. Shape is the contract RedeemPoints.parseScan reads. */
export const prizeQrPayload = ({ venueUid, djchatId, uid, prizeId }) => ({
  typ: PRIZE_QR_TYP,
  v: 1,
  venueUid,
  djchatId,
  uid,
  prizeId: prizeId || null,
});

/**
 * The typeable code, for when the camera won't scan (iPhone Safari has no
 * BarcodeDetector, so staff there always type). `venueUid:djchatId` — the
 * staff parser accepts this bare form as of 2026-10-08; before that it was
 * rejected, which is why it never worked typed.
 */
export const prizeCode = ({ venueUid, djchatId }) => `${venueUid}:${djchatId}`;

/**
 * Save a secured prize to the guest's archive. Call it the moment the prize is
 * genuinely theirs (claimPrize → 'claimed'), not on a first-come race that
 * might still be lost. Idempotent.
 */
export async function recordPrizeWin(uid, {
  venueUid, venueName, djchatId, prizeId, prizeDescription, trackTitle, trackArtist,
}) {
  if (!uid || !venueUid || !djchatId) return;
  const ids = { venueUid, djchatId, uid, prizeId };
  await setDoc(doc(db, 'users', uid, 'prizes', djchatId), {
    djchatId,
    venueUid,
    venueName: venueName || '',
    prizeId: prizeId || null,
    prizeDescription: prizeDescription || '',
    trackTitle: trackTitle || '',
    trackArtist: trackArtist || '',
    status: 'outstanding',
    wonAt: serverTimestamp(),
    qr: prizeQrPayload(ids),
    code: prizeCode(ids),
  }, { merge: true });
}

const ms = (t) => (t && typeof t.toMillis === 'function' ? t.toMillis()
  : t && typeof t.seconds === 'number' ? t.seconds * 1000 : 0);

/**
 * Live archive. Outstanding first (those are the ones a guest opens this for),
 * newest first within each. Sorted here rather than with orderBy so a
 * just-written doc — whose serverTimestamp is still pending — still shows.
 */
export function subscribePrizeArchive(uid, cb) {
  if (!uid) { cb({ outstanding: [], redeemed: [] }); return () => {}; }
  return onSnapshot(
    collection(db, 'users', uid, 'prizes'),
    (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const byNewest = (k) => (a, b) => ms(b[k]) - ms(a[k]);
      cb({
        outstanding: all.filter((p) => p.status !== 'redeemed').sort(byNewest('wonAt')),
        redeemed: all.filter((p) => p.status === 'redeemed').sort(byNewest('redeemedAt')),
      });
    },
    (err) => {
      console.warn('[prizeArchive] subscribe failed:', err?.message);
      cb({ outstanding: [], redeemed: [] });
    }
  );
}

export const prizeDate = (t) => {
  const v = ms(t);
  return v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};
